// src/utils/keyboardShortcuts.ts
import { useCallback } from 'react';
import { toast } from 'sonner';

export const useKeyboardShortcuts = (
  globalNodeEdgeUndoManager: any,
  nodes: any[],
  copyNodeToClipboard: (nodeId: string) => void,
  pasteNodeFromClipboard: (position?: { x: number; y: number } | null) => Promise<void>,
  showNodeIndex: boolean,
  showFileDialog: boolean,
  nodeMenu: any,
  paneMenu: any,
  sectionMenu: any,
  onShowSearch?: () => void,
  onTriggerGlitch?: () => void
) => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!globalNodeEdgeUndoManager) return;
      const isCtrlPressed = event.ctrlKey || event.metaKey;
      const isInDialog = showNodeIndex || showFileDialog || nodeMenu || paneMenu?.isVisible || sectionMenu?.isVisible;
      const activeElement = document.activeElement;

      const isEditingText = () => {
        if (
          activeElement &&
          (activeElement.tagName === 'INPUT' ||
           activeElement.tagName === 'TEXTAREA' ||
           (activeElement as HTMLElement).isContentEditable)
        ) {
          return true;
        }

        const selection = window.getSelection();
        if (selection && selection.toString().trim().length > 0) {
          const scrollArea = document.querySelector('[data-slot="scroll-area"]');
          if (scrollArea && scrollArea.contains(selection.anchorNode)) {
            return true;
          }
          // Allow copy if selection is inside generated-markdown
          const generatedMarkdown = document.querySelector('.generated-markdown');
          if (generatedMarkdown && generatedMarkdown.contains(selection.anchorNode)) {
            return true;
          }
        }

        return false;
      };

      const isDesktopMode = !isInDialog && !isEditingText();
      if (!isDesktopMode) return;

      if (isCtrlPressed && event.key === 'z') {
        event.preventDefault();
        globalNodeEdgeUndoManager.undo();
      } else if (isCtrlPressed && (event.key === 'y' || (event.shiftKey && event.key === 'Z'))) {
        event.preventDefault();
        globalNodeEdgeUndoManager.redo();
      } else if (isCtrlPressed && event.key === 'c') {
        const selection = window.getSelection();
        if (selection && selection.toString().trim().length > 0) {
          const scrollArea = document.querySelector('[data-slot="scroll-area"]');
          if (scrollArea && scrollArea.contains(selection.anchorNode)) {
            return;
          }
        }

        const selectedNodes = nodes.filter((node) => node.selected);
        if (selectedNodes.length === 1) {
          event.preventDefault();
          copyNodeToClipboard(selectedNodes[0].id);
        } else if (selectedNodes.length > 1) {
          toast.info('Multiple nodes selected', {
            description: 'Currently only single node copy is supported.',
          });
        }
      } else if (isCtrlPressed && event.key === 'v') {
        event.preventDefault();
        pasteNodeFromClipboard();
      } else if (isCtrlPressed && event.key === 'f') {
        event.preventDefault();
        onShowSearch?.();
      } else if (isCtrlPressed && event.key === 's') {
        event.preventDefault();
        onTriggerGlitch?.();
      }
    },
    [
      globalNodeEdgeUndoManager,
      nodes,
      copyNodeToClipboard,
      pasteNodeFromClipboard,
      showNodeIndex,
      showFileDialog,
      nodeMenu,
      paneMenu,
      sectionMenu,
      onShowSearch,
      onTriggerGlitch,
    ]
  );

  return { handleKeyDown };
};